import { FunctionComponent, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import PaymentIcon from '@mui/icons-material/Payment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import LinkIcon from '@mui/icons-material/Link';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import LogoutIcon from '@mui/icons-material/Logout';
import CircularProgress from '@mui/material/CircularProgress';
import { userService, authService } from '../../../api';
import { useApiCall } from '../../../hooks/useApiCall';
import styles from './ProfileScreen.module.css';

interface ProfileData {
  id: string;
  name: string;
  email: string;
  memberSince: string;
  profileImage?: string;
  subscription?: {
    plan: string;
    status: string;
    renewalDate: string;
  };
  notifications?: {
    email: boolean;
    sms: boolean;
    push: boolean;
    reminders: boolean;
  };
  linkedAccounts?: Array<{
    id: string;
    type: string;
    provider: string;
    email: string;
    linkedAt: string;
  }>;
  stats?: {
    savedProviders: number;
    claimsSubmitted: number;
    savingsAchieved: string;
  };
}

const ProfileScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  
  const { loading: isLoading, execute: fetchProfile } = useApiCall(userService.getProfile);
  const { execute: uploadImage } = useApiCall(userService.uploadProfileImage);
  const { execute: logout } = useApiCall(authService.logout);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    const result = await fetchProfile();
    if (result?.success && result.data) {
      setProfileData(result.data);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && profileData) {
      setIsUploadingImage(true);
      
      const result = await uploadImage(file);
      
      if (result?.success && result.data) {
        setProfileData({
          ...profileData,
          profileImage: result.data.profileImage
        });
      }
      
      setIsUploadingImage(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const profileSections = [
    {
      title: 'Manage',
      items: [
        {
          label: 'Contact Information',
          icon: <ContactMailIcon />,
          path: '/profile/contact-information'
        },
        {
          label: 'Subscription',
          icon: <SubscriptionsIcon />,
          path: '/profile/subscription'
        },
        {
          label: 'Notifications',
          icon: <NotificationsIcon />,
          path: '/profile/notifications'
        },
        {
          label: 'Payment Methods',
          icon: <PaymentIcon />,
          path: '/profile/payment-methods'
        },
        {
          label: 'Linked Accounts',
          icon: <LinkIcon />,
          path: '/profile/linked-accounts'
        },
        {
          label: 'Invite Friends',
          icon: <GroupAddIcon />,
          path: '/profile/invite-friends'
        }
      ]
    },
    {
      title: 'Support',
      items: [
        {
          label: 'Help & Support',
          icon: <SupportAgentIcon />,
          path: '/profile/support'
        },
        {
          label: 'Privacy Policy',
          icon: <PrivacyTipIcon />,
          path: '/profile/privacy'
        }
      ]
    }
  ];

  if (isLoading) {
    return (
      <div className={styles.profileScreen}>
        <div className={styles.loadingContainer}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className={styles.profileScreen}>
        <div className={styles.errorContainer}>
          <p>Failed to load profile data</p>
          <button onClick={fetchProfileData}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.profileScreen}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <h1 className={styles.headerTitle}>Profile</h1>
        <div className={styles.headerSpacer} />
      </div>

      <div className={styles.profileInfo}>
        <div className={styles.profileImageWrapper}>
          <img 
            className={styles.profileImage} 
            alt={profileData.name} 
            src={profileData.profileImage}
          />
          <button 
            className={styles.editImageButton} 
            onClick={handleImageClick}
            disabled={isUploadingImage}
          >
            {isUploadingImage ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <CameraAltIcon />
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            style={{ display: 'none' }}
          />
        </div>
        <div className={styles.profileDetails}>
          <h2 className={styles.userName}>{profileData.name}</h2>
          <p className={styles.userEmail}>{profileData.email}</p>
          <p className={styles.memberSince}>Member since {profileData.memberSince}</p>
        </div>
      </div>

      {profileData.stats && (
        <div className={styles.statsContainer}>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{profileData.stats.savedProviders}</span>
            <span className={styles.statLabel}>Saved Providers</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{profileData.stats.claimsSubmitted}</span>
            <span className={styles.statLabel}>Claims Submitted</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{profileData.stats.savingsAchieved}</span>
            <span className={styles.statLabel}>Total Savings</span>
          </div>
        </div>
      )}

      <div className={styles.sectionsContainer}>
        {profileSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className={styles.section}>
            <h3 className={styles.sectionTitle}>{section.title}</h3>
            {section.items.map((item, itemIndex) => (
              <button
                key={itemIndex}
                className={styles.sectionItem}
                onClick={() => navigate(item.path)}
              >
                <div className={styles.itemLeft}>
                  <div className={styles.itemIcon}>{item.icon}</div>
                  <span className={styles.itemLabel}>{item.label}</span>
                </div>
                <ChevronRightIcon className={styles.chevronIcon} />
              </button>
            ))}
          </div>
        ))}

        <div className={styles.section}>
          <button className={styles.logoutButton} onClick={handleLogout}>
            <LogoutIcon />
            <span>Log Out</span>
          </button>
        </div>
      </div>

      <div className={styles.appInfo}>
        <p className={styles.version}>Mario Health v0.1.0</p>
      </div>
    </div>
  );
};

export default ProfileScreen;